import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRightIcon, ClockIcon, CheckCircleIcon, CalendarIcon,
  ChatBubbleLeftRightIcon, FireIcon, StarIcon, BoltIcon,
  UserGroupIcon, AcademicCapIcon, BriefcaseIcon,
} from '@heroicons/react/24/outline';

// Import hero image
import heroImage from '../../assets/images/fitneseqp10.png';

// ─── FONT IMPORT ──────────────────────────────────────────────────────────────
const FONTS = `@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;900&family=DM+Sans:wght@300;400;500;600&display=swap');`;

// ─── WHATSAPP NUMBER ─────────────────────────────────────────────────────────
const WA_NUMBER = '254117110000';

// ─── PACKAGES DATA ────────────────────────────────────────────────────────────
const PACKAGES = [
  {
    id: 'day-pass',
    title: 'Day Pass',
    subtitle: '1 Day',
    icon: BoltIcon,
    accentColor: '#e63946',
    price: 1500,
    features: [
      'Access to all classes',
      'Full gym access',
      'Free WiFi access',
      'General trainer',
      '2 hours free parking',
    ],
    description: 'Perfect for trying out PowerCore or for short visits. Get full access to everything for one day.',
  },
  {
    id: '10-day',
    title: '10 Day Pass',
    subtitle: '10 Days',
    icon: CalendarIcon,
    accentColor: '#f4a261',
    price: 3500,
    features: [
      'Access to all classes',
      'Full gym access',
      'Free WiFi access',
      '2 hours free parking',
    ],
    description: 'Great for short-term fitness goals or visitors. Valid for 10 consecutive days.',
  },
  {
    id: 'monthly',
    title: 'Monthly',
    subtitle: '1 Month',
    icon: FireIcon,
    accentColor: '#800020',
    price: 6500,
    features: [
      'All classes included',
      'Sauna and steam room access',
      'Full gym access',
      'Free WiFi',
      '2 hours free parking',
    ],
    description: 'Full access to everything PowerCore has to offer.',
  },
  {
    id: '3-months',
    title: 'Quarterly',
    subtitle: '3 Months',
    icon: StarIcon,
    accentColor: '#2a9d8f',
    price: 18500,
    features: [
      'All classes included',
      'Sauna and steam room access',
      'Full gym access',
      'Free WiFi',
      '2 hours free parking',
    ],
    description: 'Save with a 3-month commitment. Perfect for consistent training.',
  },
  {
    id: '6-months',
    title: 'Bi-Annual',
    subtitle: '6 Months',
    icon: StarIcon,
    accentColor: '#0077b6',
    price: 35000,
    features: [
      'All classes included',
      'Sauna and steam room access',
      'Full gym access',
      'Free WiFi',
      '2 hours free parking',
    ],
    description: 'Great value for long-term fitness goals. Significant savings.',
  },
  {
    id: 'yearly',
    title: 'Annual',
    subtitle: '1 Year',
    icon: StarIcon,
    accentColor: '#6a0572',
    price: 67000,
    features: [
      'All classes included',
      'Sauna and steam room access',
      'Full gym access',
      'Free WiFi',
      '2 hours free parking',
    ],
    description: 'Best value! Get a full year of unlimited fitness for less than KES 5,600/month.',
  },
  {
    id: 'couple',
    title: 'Couple Package',
    subtitle: 'Per Person',
    icon: UserGroupIcon,
    accentColor: '#e63946',
    price: 5500,
    features: [
      'All classes included',
      'Sauna and steam room access',
      'Full gym access',
      'Free WiFi',
      '2 hours free parking',
    ],
    description: 'Train together, stay together. Special rate for couples (minimum 2 people).',
  },
  {
    id: 'corporate',
    title: 'Corporate Package',
    subtitle: '15+ Members',
    icon: BriefcaseIcon,
    accentColor: '#800020',
    price: 5500,
    features: [
      'All classes included',
      'Sauna and steam room access',
      'Full gym access',
      'Free WiFi',
      '2 hours free parking',
    ],
    description: 'Corporate wellness program. Minimum 15 members from the same company.',
  },
  {
    id: 'student',
    title: 'Student Package',
    subtitle: 'Age ≤ 21',
    icon: AcademicCapIcon,
    accentColor: '#f4a261',
    price: 4500,
    features: [
      'Access 10am - 4pm only',
      'All classes during hours',
      'Full gym access',
      'Free WiFi',
    ],
    description: 'Special rate for students. Valid ID required. Ages 21 and under only.',
  },
  {
    id: 'general-training',
    title: 'General Training',
    subtitle: 'Monthly',
    icon: UserGroupIcon,
    accentColor: '#f4a261',
    price: 3000,
    features: [
      'Motivation from peers and trainer',
      'More affordable than one-on-one',
      'Build community while you train',
      'Full access to all gym equipment',
      'Access to all classes',
      'Free WiFi access',
      '2 hours free parking',
    ],
    description: 'Train with others, stay motivated, and achieve your fitness goals together.',
    isAddOn: true,
  },
  {
    id: 'personal-training',
    title: 'Personal Training',
    subtitle: 'Monthly',
    icon: FireIcon,
    accentColor: '#800020',
    price: 10000,
    features: [
      'One-on-one coaching with certified trainer',
      'Personalized workout plans',
      'Form correction and injury prevention',
      'Nutritional guidance',
      'Faster results with expert coaching',
      'Full gym access',
      'Access to all classes',
      'Sauna and steam room access',
      'Free WiFi',
      '2 hours free parking',
    ],
    description: 'Get personalized attention and faster results with dedicated one-on-one coaching.',
    popular: true,
    isAddOn: true,
  },
];

// ─── EXTRA SERVICES ───────────────────────────────────────────────────────────
const EXTRA_SERVICES = [
  {
    title: 'Private Locker',
    price: '1,000',
    period: 'per month',
    description: 'Your own private locker for secure storage',
  },
  {
    title: 'Laundry Service',
    price: 'Negotiable',
    period: 'small fee',
    description: 'Keep your gym gear fresh and clean',
  },
  {
    title: 'Towel Service',
    price: '2,500',
    period: 'per month',
    description: 'Fresh towels provided daily',
  },
];

// ─── NON-MEMBER / VISITOR RATES (per visit, no membership required) ─────────
const VISITOR_RATES = [
  {
    title: 'Steam Room',
    price: '1,000',
    period: 'per visit',
    description: 'Single-visit access to our steam room — no membership needed',
  },
  {
    title: 'Sauna',
    price: '1,000',
    period: 'per visit',
    description: 'Single-visit access to our infrared sauna — no membership needed',
  },
  {
    title: 'Classes',
    price: '500',
    period: 'per visit',
    description: 'Drop in to a single class — no membership needed',
  },
];

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay },
  viewport: { once: true },
});

const buildWhatsAppMessage = (pkg) => {
  const addOnNote = pkg.isAddOn ? '\n\n⚠️ Note: Training packages are ADD-ON services only. You must have an active base membership package to add training.\n' : '';
  const msg = `Hi PowerCore! 👋\n\nI'm interested in the *${pkg.title}* (${pkg.subtitle}) package.\n\nPackage: ${pkg.title}\nPrice: KES ${pkg.price.toLocaleString()}${addOnNote}\n\nPlease let me know how I can sign up. Thank you!`;
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`;
};

// ─── PACKAGE CARD ────────────────────────────────────────────────────────────
const PackageCard = ({ pkg, index }) => {
  const Icon = pkg.icon;
  const whatsappUrl = buildWhatsAppMessage(pkg);

  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: (index % 3) * 0.08 }}
      viewport={{ once: true }}
      className={`group rounded-2xl overflow-hidden bg-white shadow-md hover:shadow-xl transition-all duration-500 hover:-translate-y-1.5 flex flex-col relative ${
        pkg.popular ? 'ring-2 ring-red-500' : ''
      }`}
    >
      {pkg.popular && (
        <div className="absolute top-0 right-0 z-10">
          <div className="bg-gradient-to-r from-red-600 to-red-700 text-white text-xs font-bold px-3 py-1 rounded-bl-2xl flex items-center gap-1">
            <FireIcon className="h-3 w-3" />
            MOST POPULAR
          </div>
        </div>
      )}

      {/* Add-on badge */}
      {pkg.isAddOn && (
        <div className="absolute top-0 left-0 z-10">
          <div className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-br-2xl flex items-center gap-1">
            <StarIcon className="h-3 w-3" />
            ADD-ON SERVICE
          </div>
        </div>
      )}

      {/* Header */}
      <div className="relative p-5" style={{ background: `linear-gradient(135deg, ${pkg.accentColor}15, ${pkg.accentColor}05)` }}>
        <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ background: pkg.accentColor }}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <p className="text-xs font-bold tracking-wider uppercase mb-1" style={{ color: pkg.accentColor }}>{pkg.subtitle}</p>
        <h3 className="font-black text-gray-900 text-xl leading-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
          {pkg.title}
        </h3>
        <div className="mt-2">
          <span className="font-black text-2xl" style={{ color: pkg.accentColor, fontFamily: "'Barlow Condensed', sans-serif" }}>
            KES {pkg.price.toLocaleString()}
          </span>
        </div>
      </div>

      {/* Features */}
      <div className="p-5 flex-1">
        <ul className="space-y-2">
          {pkg.features.map((feature, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
              <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: pkg.accentColor }} />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* WhatsApp Button */}
      <div className="px-5 pb-5">
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold text-white transition-all hover:scale-[1.02]"
          style={{ background: '#25D366' }}
        >
          <ChatBubbleLeftRightIcon className="h-4 w-4" />
          Enquire on WhatsApp
        </a>
      </div>
    </motion.div>
  );
};

// ─── EXTRA SERVICE CARD (also reused for visitor/non-member rates) ─────────
const ExtraServiceCard = ({ service, index, accentColor = '#800020' }) => {
  const whatsappMessage = `Hi PowerCore! 👋\n\nI'm interested in the *${service.title}* service.\nPrice: ${service.price} ${service.period}\n\nPlease let me know more details. Thank you!`;
  const whatsappUrl = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(whatsappMessage)}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true }}
      className="rounded-2xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-all p-5 text-center"
    >
      <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ background: `${accentColor}15`, color: accentColor }}>
        <StarIcon className="h-6 w-6" />
      </div>
      <h3 className="font-bold text-gray-900 mb-1">{service.title}</h3>
      <p className="font-black text-lg" style={{ color: accentColor, fontFamily: "'Barlow Condensed', sans-serif" }}>
        KES {service.price}
      </p>
      <p className="text-xs text-gray-400 mb-3">{service.period}</p>
      <p className="text-xs text-gray-500 mb-4">{service.description}</p>
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 text-sm font-semibold transition-all hover:gap-3"
        style={{ color: '#25D366' }}
      >
        Enquire <ArrowRightIcon className="h-3.5 w-3.5" />
      </a>
    </motion.div>
  );
};

// ─── INFO PANEL (Timings) ─────────────────────────────────────────────────────
const InfoPanel = () => (
  <div className="grid gap-5">
    {/* Class Schedule */}
    <div className="rounded-2xl overflow-hidden shadow-sm" style={{ background: '#1a1a2e' }}>
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#800020' }}>
            <CalendarIcon className="h-4 w-4 text-white" />
          </div>
          <h3 className="font-black text-white text-sm uppercase tracking-wider" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            Class Schedule
          </h3>
        </div>
        <div className="space-y-3">
          <div className="flex flex-col gap-1 border-b border-white/10 pb-2">
            <span className="text-white/60 text-xs font-medium">Monday – Friday</span>
            <div className="flex flex-wrap gap-1">
              <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: '#800020', color: 'white' }}>6:00 AM – 7:00 AM</span>
              <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: '#800020', color: 'white' }}>6:00 PM – 7:00 PM</span>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-white/60 text-xs font-medium">Saturday</span>
            <span className="text-xs font-bold px-2 py-0.5 rounded-full inline-block" style={{ background: '#800020', color: 'white' }}>8:00 AM – 9:00 AM</span>
          </div>
        </div>
      </div>
    </div>

    {/* Sauna & Steam Schedule */}
    <div className="rounded-2xl overflow-hidden shadow-sm" style={{ background: '#0d1b2a' }}>
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#f4a261' }}>
            <FireIcon className="h-4 w-4 text-white" />
          </div>
          <h3 className="font-black text-white text-sm uppercase tracking-wider" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            Sauna & Steam
          </h3>
        </div>
        <div className="space-y-2">
          <div className="flex flex-col gap-1 border-b border-white/10 pb-2">
            <span className="text-white/60 text-xs font-medium">Monday – Saturday</span>
            <div className="flex flex-wrap gap-1">
              <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: '#f4a261', color: '#1a1a2e' }}>5:00 AM – 8:00 AM</span>
              <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: '#f4a261', color: '#1a1a2e' }}>5:00 PM – 8:00 PM</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Opening Hours */}
    <div className="rounded-2xl overflow-hidden shadow-sm" style={{ background: '#800020' }}>
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
            <ClockIcon className="h-4 w-4 text-white" />
          </div>
          <h3 className="font-black text-white text-sm uppercase tracking-wider" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            Opening Hours
          </h3>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between border-b border-white/10 pb-2">
            <span className="text-white/70 text-xs">Monday – Friday</span>
            <span className="text-white text-xs font-bold">4:00 AM – 10:00 PM</span>
          </div>
          <div className="flex items-center justify-between border-b border-white/10 pb-2">
            <span className="text-white/70 text-xs">Saturday</span>
            <span className="text-white text-xs font-bold">6:00 AM – 9:00 PM</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-white/70 text-xs">Sunday & Holidays</span>
            <span className="text-white text-xs font-bold">8:00 AM – 8:00 PM</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT - Pricing
// ═══════════════════════════════════════════════════════════════════════════════
const Pricing = () => {
  const gridRef = useRef(null);
  const scrollToGrid = () => gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  // Split packages into groups
  const standardPackages = PACKAGES.filter(p => !['couple', 'corporate', 'student', 'general-training', 'personal-training'].includes(p.id));
  const specialPackages = PACKAGES.filter(p => ['couple', 'corporate', 'student'].includes(p.id));
  const trainingPackages = PACKAGES.filter(p => ['general-training', 'personal-training'].includes(p.id));

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <style>{FONTS}</style>

      {/* ══════════════════════════════════════════════════════
          1. HERO - Full image visible, no red overflow
      ══════════════════════════════════════════════════════ */}
      <section className="relative w-full h-[450px] overflow-hidden">
        <img
          src={heroImage}
          alt="PowerCore Pricing" 
          className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0"
          style={{ background: 'linear-gradient(120deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 55%, rgba(0,0,0,0.2) 100%)' }} />
        <div className="absolute inset-0 flex flex-col justify-center px-6 sm:px-12">
          <motion.div {...fade(0.1)} className="max-w-2xl">
            <span className="inline-block text-xs font-bold tracking-[0.25em] uppercase px-3 py-1 rounded-full mb-4 border border-white/30 text-white/80">
              Membership Plans
            </span>
            <h1 className="font-black text-white mb-3 leading-none"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(2rem,5vw,3.8rem)', letterSpacing: '-0.02em' }}>
              INVEST IN <span style={{ color: '#e8a0b0' }}>YOURSELF</span>
            </h1>
            <p className="text-white/80 text-sm leading-relaxed max-w-lg">
              Flexible plans designed to fit your goals, schedule and budget. Choose what works best for you.
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              <button onClick={scrollToGrid}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-bold text-sm text-white transition-all hover:scale-105"
                style={{ background: '#800020', boxShadow: '0 4px 20px rgba(128,0,32,0.4)' }}>
                View Plans <ArrowRightIcon className="h-4 w-4" />
              </button>
              <Link to="/activities"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-bold text-sm text-white border-2 border-white/40 hover:bg-white/10 transition-all">
                View Classes
              </Link>
            </div>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16" style={{ background: 'linear-gradient(to top, #faf8f8, transparent)' }} />
      </section>

      {/* ══════════════════════════════════════════════════════
          2. PRICING PLANS GRID + INFO SIDEBAR
      ══════════════════════════════════════════════════════ */}
      <section ref={gridRef} className="py-14" style={{ background: '#faf8f8' }}>
        <div className="container mx-auto px-4 max-w-7xl">
          <motion.div {...fade(0)} className="mb-10">
            <span className="text-xs font-bold tracking-[0.2em] uppercase mb-2 inline-block" style={{ color: '#800020' }}>
              Membership Plans
            </span>
            <h2 className="font-black text-gray-900"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(1.8rem,3.5vw,2.8rem)' }}>
              CHOOSE YOUR PLAN
            </h2>
            <p className="text-gray-500 text-sm max-w-md mt-2">
              Click Enquire on any plan to start your fitness journey via WhatsApp.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8 items-start">
            {/* Left: Package Cards */}
            <div className="lg:col-span-2">
              {/* Standard Plans */}
              <div className="mb-10">
                <h3 className="font-black text-gray-800 mb-4 text-lg" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  STANDARD PLANS
                </h3>
                <div className="grid sm:grid-cols-2 gap-5">
                  {standardPackages.map((pkg, i) => (
                    <PackageCard key={pkg.id} pkg={pkg} index={i} />
                  ))}
                </div>
              </div>

              {/* Special Plans */}
              <div className="mb-10">
                <h3 className="font-black text-gray-800 mb-4 text-lg" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  SPECIAL PACKAGES
                </h3>
                <div className="grid sm:grid-cols-2 gap-5">
                  {specialPackages.map((pkg, i) => (
                    <PackageCard key={pkg.id} pkg={pkg} index={i} />
                  ))}
                </div>
              </div>

              {/* Training Charges - After Special Packages */}
              <div className="mb-10">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <h3 className="font-black text-gray-800 text-lg" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                    TRAINING CHARGES
                  </h3>
                  <div className="bg-amber-100 border-l-4 border-amber-500 px-3 py-1.5 rounded-r-md">
                    <p className="text-amber-800 text-xs font-medium flex items-center gap-1">
                      <span className="text-base">⚠️</span> Add-on only — requires an active base membership package
                    </p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  {trainingPackages.map((pkg, i) => (
                    <PackageCard key={pkg.id} pkg={pkg} index={i} />
                  ))}
                </div>
                <div className="mt-3 text-center">
                  <p className="text-xs text-gray-500 italic">
                    *Training packages cannot be purchased standalone. You must have an active Standard or Special package to add training.
                  </p>
                </div>
              </div>

              {/* Non-Member / Visitor Rates */}
              <div className="mt-10">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <h3 className="font-black text-gray-800 text-lg" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                    NON-MEMBER RATES
                  </h3>
                  <div className="bg-[#800020]/5 border-l-4 border-[#800020] px-3 py-1.5 rounded-r-md">
                    <p className="text-[#800020] text-xs font-medium flex items-center gap-1">
                      <span className="text-base">ℹ️</span> Pay-as-you-go — no membership required
                    </p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  {VISITOR_RATES.map((service, i) => (
                    <ExtraServiceCard key={service.title} service={service} index={i} accentColor="#800020" />
                  ))}
                </div>
                <div className="mt-3 text-center">
                  <p className="text-xs text-gray-500 italic">
                    *Visitor rates are charged per single visit and do not include gym floor access.
                  </p>
                </div>
              </div>

              {/* Extra Services */}
              <div className="mt-10">
                <h3 className="font-black text-gray-800 mb-4 text-lg" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  EXTRA SERVICES
                </h3>
                <div className="grid sm:grid-cols-3 gap-4">
                  {EXTRA_SERVICES.map((service, i) => (
                    <ExtraServiceCard key={service.title} service={service} index={i} />
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Info Panels */}
            <motion.div {...fade(0.2)} className="lg:sticky lg:top-8">
              <InfoPanel />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          3. CTA - Ready to Start
      ══════════════════════════════════════════════════════ */}
      <section className="py-14 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg,#800020 0%,#3d0010 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full filter blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl" />
        </div>
        <div className="container mx-auto px-4 max-w-7xl text-center relative z-10">
          <motion.div {...fade(0)}>
            <span className="text-xs font-bold tracking-[0.2em] uppercase px-4 py-1.5 rounded-full inline-block mb-4 bg-white/20 text-white/80">
              Limited Time Offer
            </span>
            <h2 className="font-black text-white mb-4 leading-none"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(2rem,4vw,3.5rem)' }}>
              READY TO START YOUR <span className="text-yellow-400">JOURNEY?</span>
            </h2>
            <p className="text-white/75 mb-8 max-w-lg mx-auto text-sm leading-relaxed">
              Join PowerCore today and transform your life. No contracts. No excuses. Just results.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/activities"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full font-black text-sm text-gray-900 bg-white transition-all hover:scale-105 hover:shadow-2xl">
                View Classes <ArrowRightIcon className="h-4 w-4" />
              </Link>
              <a
                href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent("Hi PowerCore! I'd like to know more about your membership options.")}`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full font-bold text-sm text-white border-2 border-white/40 hover:bg-white/10 transition-all">
                <ChatBubbleLeftRightIcon className="h-4 w-4" />
                Chat on WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;